import React from 'react';
import { PALETTE } from '../charts';
import { SeriesCard } from './MetricCard';
import { peakValue } from '../../observability/contract';

/* The database over time: the Platform's Database tab (DatabasePanel), and the load
   tests' report over a run's window with its phases drawn (`formatX`, `timeline`, see
   charts/phases.jsx; `past`: the readouts are the values at the window's end). `data` is
   the Database view (services/observability/views.database_window). A report stored
   without the Platform's view (`legacy`) has only the series the run's worker kept, so a
   card whose series it never had is left out rather than drawn empty. */
export default function DatabaseCharts({ data, range, loading, formatX, timeline, past = false }) {
  const a = data?.aurora || {};
  const c = data?.current || {};
  const peakAcu = peakValue(data?.series?.acu_writer) || 0;
  // A card is drawn for a series the view carries, and a line for each of its series that
  // is there: the live view leaves out what the environment does not run (no RDS Proxy, no
  // reader), a stored report what the run's worker never kept. Before the first answer
  // (`data` undefined) every card is drawn, loading.
  const has = (...keys) => !data || keys.some(k => k in (data.series || {}));
  const present = lines => (data ? lines.filter(l => l.key in (data.series || {})) : lines);
  const common = {
    range, loading, formatX, timeline, past, timestamps: data?.timestamps, series: data?.series,
  };
  // The half-width cards drawn before Database size. An odd count leaves an empty half
  // beside the last one (no RDS Proxy, say, puts Commit latency alone on its row), and
  // Database size takes it instead of a row of its own.
  const halves = [
    has('acu_writer', 'acu_reader'), has('connections'), has('cpu_writer', 'cpu_reader'),
    has('commit_latency_ms'), has('replica_lag_ms'),
    has('proxy_client_connections', 'proxy_db_connections'),
  ].filter(Boolean).length;
  return (
    <div className="dashboard-grid">
      {has('acu_writer', 'acu_reader') && (
        <SeriesCard
          {...common}
          title="Capacity (ACU)"
          subtitle={a.max_acu ? `Serverless v2 capacity per instance · max ${a.max_acu} ACU` : 'Serverless v2 capacity per instance'}
          unit="acu"
          lines={present([
            { key: 'acu_writer', label: 'Writer', color: PALETTE[2] },
            { key: 'acu_reader', label: 'Reader', color: PALETTE[7] },
          ])}
          /* The ceiling is drawn only once usage is within reach of it. At 2 ACU against
             a 64 ACU maximum the line would flatten the curve that matters into the
             baseline, and the maximum is in the subtitle either way. */
          refLines={a.max_acu && peakAcu >= a.max_acu * 0.5
            ? [{ value: a.max_acu, label: `max ${a.max_acu} ACU`, color: 'var(--danger)' }] : undefined}
          help="Sustained time near the maximum means the cluster is capacity-bound: raise the maximum before raising proxy replica counts, since connections scale with capacity."
        />
      )}
      {has('connections') && (
        <SeriesCard
          {...common}
          title="Connections" subtitle="In use against max_connections"
          lines={[{ key: 'connections', label: 'In use', color: PALETTE[7] }]}
          refLines={c.max_connections ? [{ value: c.max_connections, label: 'max_connections', color: 'var(--danger)' }] : undefined}
        />
      )}
      {has('cpu_writer', 'cpu_reader') && (
        <SeriesCard
          {...common}
          title="CPU" subtitle="Per instance" unit="percent" yMax={100}
          lines={present([
            { key: 'cpu_writer', label: 'Writer', color: PALETTE[0] },
            { key: 'cpu_reader', label: 'Reader', color: PALETTE[1] },
          ])}
        />
      )}
      {has('commit_latency_ms') && (
        <SeriesCard
          {...common}
          title="Commit latency" subtitle="Writer, average per bucket" unit="ms"
          lines={[{ key: 'commit_latency_ms', label: 'Commit', color: PALETTE[3] }]}
        />
      )}
      {has('replica_lag_ms') && (
        <SeriesCard
          {...common}
          title="Replica lag" subtitle="How far the reader trails the writer" unit="ms"
          lines={[{ key: 'replica_lag_ms', label: 'Lag', color: PALETTE[6] }]}
        />
      )}
      {has('proxy_client_connections', 'proxy_db_connections') && (
        <SeriesCard
          {...common}
          title="RDS Proxy connections" subtitle="Clients in, pooled connections out"
          lines={present([
            { key: 'proxy_client_connections', label: 'Client', color: PALETTE[0] },
            { key: 'proxy_db_connections', label: 'Database', color: PALETTE[5] },
          ])}
        />
      )}
      {has('db_size_bytes') && (
        <SeriesCard
          {...common}
          title="Database size" subtitle="Total size on disk, sampled every 2 minutes" unit="bytes"
          span={halves % 2 ? 6 : 12}
          lines={[{ key: 'db_size_bytes', label: 'Size', color: PALETTE[8] }]}
          help="Watch this against the retention windows in Data Governance. Growth that survives a retention sweep means a table has no retention policy, or its deletes are not being reclaimed."
        />
      )}
    </div>
  );
}
